import PermissionHelper from "@/permissions/PermissionHelper";
import { ActionButtonEnum } from "@/permissions/ActionButtonEnum";

/**
 * Does this menu / search / landing entry pass the action permission it declares?
 *
 * An entry may carry `permission-enum` (the search + nav configs, which hold the enum KEY as a
 * string) or `action` (the Reports landing's own item list, which holds the enum VALUE). When it
 * does, that action gates the entry **in addition to** whatever menu or route check the caller
 * already performs; when it does not, the entry is unaffected.
 *
 * This lives in one place on purpose, and the reason is not testability — both callers are testable
 * (`reportsLandingGate.spec.ts` mounts the landing page; `reportsSearchGate.spec.ts` imports the
 * layout and runs the real `initSearchResultItems`). The reason is that the two surfaces which can
 * reach a report page — the Reports landing (`pages/reports/Index.vue`) and the toolbar global search
 * (`layouts/DefaultLayout.initSearchResultItems`) — are separate hardcoded lists, and PMS-881 was
 * caused by exactly that: a page registered in one and not the other, with a guard reading only one
 * copy. One gate makes the two agree structurally instead of by coincidence.
 *
 * Fails closed on an unrecognised code: `canAccessButton` matches a permission row by `code`, so a
 * typo resolves to nothing and hides the entry rather than exposing it.
 */
export interface ActionGatedEntry {
  "permission-enum"?: string;
  action?: string;
}

export function passesEntryActionGate(item: ActionGatedEntry | null | undefined): boolean {
  const declared = item ? (item["permission-enum"] || item.action) : undefined;
  if (!declared) {
    return true;
  }
  // Accept either the enum key or its value; for this string enum the two coincide. Reading through
  // ActionButtonEnum absorbs a changed enum VALUE under a stable key. It does NOT protect against a
  // renamed key: that lookup misses and the `|| declared` fallback keeps the stale literal, which then
  // matches no permission row — wrong, but closed rather than open.
  const action = (ActionButtonEnum as Record<string, string>)[declared] || declared;
  return PermissionHelper.canAccessButton(action as ActionButtonEnum);
}

export default passesEntryActionGate;
